import PanelEntry from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/PanelEntry';
import React, { useState, useEffect } from 'react';
import { useOCPStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useOCPStore';
import { Button, Icon, SelectMenu, TextField, Typography } from '@scale/scaleui';
import {
  ConditionType,
  LoadBalancerCondition,
  OnboardingFlowNode,
  OnboardingFlowTransition,
} from '@scale/shared/onboardingControlPanel';
import { useDraftFlowStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useDraftFlowStore';
import _ from 'lodash';

interface Props {
  allowAddOrDeleteCondition?: boolean;
}

export const LoadBalancerRouting: React.FC<Props> = ({ allowAddOrDeleteCondition = true }) => {
  const { currentFlow, detailTarget } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    detailTarget: state.detailTarget,
  }));

  const { draftFlows, editDraftEntityValue, addNode, deleteDraftEntity } = useDraftFlowStore(
    state => ({
      draftFlows: state.draftFlows,
      editDraftEntityValue: state.editDraftEntityValue,
      addNode: state.addNode,
      deleteDraftEntity: state.deleteDraftEntity,
    }),
  );
  const flow = draftFlows[currentFlow?._id ?? ''] ?? currentFlow;
  const [possibleTransitions, setPossibleTransitions] = useState<OnboardingFlowTransition[]>(
    flow.edges.filter(edge => edge.sourceNode === detailTarget?.name),
  );

  useEffect(() => {
    setPossibleTransitions(flow.edges.filter(edge => edge.sourceNode === detailTarget?.name));
  }, [flow.edges, detailTarget]);

  const addCondition = () => {
    if (currentFlow && detailTarget) {
      // add a node and default conditions
      addNode(currentFlow?._id, detailTarget as OnboardingFlowNode);
    }
  };

  const handleTypeChange = (val: string, i: number) => {
    if (currentFlow && possibleTransitions) {
      const updatedTransitions = [...possibleTransitions];

      for (let index = 0; index < updatedTransitions.length; index++) {
        const updatedCondition =
          (updatedTransitions[index].conditions[0] as LoadBalancerCondition) ?? {};
        updatedCondition.type = ConditionType.LoadBalancer;
        if (val === 'cap' && (!_.isUndefined(updatedCondition.percentage) || index === i)) {
          updatedCondition.capacity = 100;
          updatedCondition.percentage = undefined;
        } else if (val === 'percent' && _.isUndefined(updatedCondition.percentage)) {
          updatedCondition.percentage = 100 / updatedTransitions.length;
          updatedCondition.capacity = undefined;
        } else if (val === 'no cap' && (_.isUndefined(updatedCondition.capacity) || index === i)) {
          updatedCondition.capacity = undefined;
          updatedCondition.percentage = undefined;
        }

        updatedTransitions[index].conditions[0] = updatedCondition;
        editDraftEntityValue(currentFlow._id, updatedTransitions[index], updatedTransitions[index]);
      }
      setPossibleTransitions(updatedTransitions);
    }
  };

  const handleConditionChange = (index: number, capVal?: string, percentVal?: string) => {
    if (currentFlow && possibleTransitions) {
      // Clone the current state to avoid direct mutations
      const updatedTransitions = [...possibleTransitions];

      const updatedTransition = { ...updatedTransitions[index] };
      // Update the capacity for the condition at the specified index
      const updatedCondition: LoadBalancerCondition = {
        type: ConditionType.LoadBalancer,
        capacity: capVal ? parseInt(capVal, 10) || 100 : undefined,
        percentage: percentVal ? parseInt(percentVal, 10) || 100 : undefined,
      };

      // Update the conditions array with the modified condition
      updatedTransition.conditions[0] = updatedCondition;
      updatedTransitions[index] = updatedTransition;
      setPossibleTransitions(updatedTransitions);
      editDraftEntityValue(currentFlow._id, updatedTransition, updatedTransition);
    }
  };

  const deleteCondition = (index: number) => {
    if (currentFlow && possibleTransitions) {
      const destinationNodeName = possibleTransitions[index].destNode;
      const destinationNode = flow.nodes.find(node => node.name === destinationNodeName);
      deleteDraftEntity(currentFlow?._id, destinationNode ?? null);
    }
  };

  return (
    <>
      <Typography.Paragraph3>
        Percentage-Based Routing: Contributors will be probabilistically distributed based on the
        percentages set below. The percentage of the final node is automatically calculated and must
        be greater than 0%. The sum of all percentages must be 100%.
      </Typography.Paragraph3>
      <Typography.Paragraph3>
        Cap-Based Routing: Contributors will be evenly distributed to the following destinations
        until the count of users matches capacity. Count is defined as the number of users who have
        gone to the destination AND were online within the past 7 days. There must be at least one
        destination with no cap.
      </Typography.Paragraph3>
      <Typography.Paragraph3>
        All routing rules must either be percentage-based or cap-based, mixing the two is not
        supported.
      </Typography.Paragraph3>
      {possibleTransitions &&
        possibleTransitions.map((transition: OnboardingFlowTransition, i: number) => (
          <div key={i} className="flex gap-2 items-center">
            <Typography.Paragraph2>{transition.destNode}</Typography.Paragraph2>
            {
              <SelectMenu
                options={[
                  { id: 'percent', name: 'percent' },
                  { id: 'cap', name: 'cap' },
                  { id: 'no cap', name: 'no cap' },
                ]}
                selectedOptionId={
                  !_.isUndefined(
                    (possibleTransitions[i].conditions[0] as LoadBalancerCondition)?.capacity,
                  )
                    ? 'cap'
                    : !_.isUndefined(
                        (possibleTransitions[i].conditions[0] as LoadBalancerCondition)?.percentage,
                      )
                    ? 'percent'
                    : 'no cap'
                }
                setSelected={val => handleTypeChange(val, i)}
              />
            }
            {(possibleTransitions[i].conditions[0] as LoadBalancerCondition)?.capacity !==
              undefined && (
              <TextField
                id="capacity"
                value={(possibleTransitions[i].conditions[0] as LoadBalancerCondition).capacity}
                placeholder="Enter Capacity"
                onChange={e => handleConditionChange(i, e.target.value, undefined)}
              />
            )}
            {(possibleTransitions[i].conditions[0] as LoadBalancerCondition)?.percentage !==
              undefined && (
              <TextField
                id="percent"
                value={(possibleTransitions[i].conditions[0] as LoadBalancerCondition).percentage}
                placeholder="Enter Percent"
                onChange={e => handleConditionChange(i, undefined, e.target.value)}
              />
            )}
            {allowAddOrDeleteCondition && (
              <div className="ml-auto">
                <Icon
                  rounded
                  size="sm"
                  icon="minus"
                  variant="neutral"
                  onClick={() => deleteCondition(i)}
                />
              </div>
            )}
          </div>
        ))}

      {allowAddOrDeleteCondition && (
        <Button variant="transparent" className="w-fit ml-2" onClick={addCondition}>
          + Add Destination
        </Button>
      )}
    </>
  );
};

const LoadBalancer: React.FC<Props> = () => {
  return (
    <div>
      <PanelEntry title={'Load Balancer Routing'}>
        <LoadBalancerRouting />
      </PanelEntry>
    </div>
  );
};

export default LoadBalancer;
